import { FC, useMemo } from 'react';
import { OwnershipSplitType, OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import { EquityItem } from '../../CapitalStructureItemFormERF2';
import { useWatch } from 'react-hook-form';
import { useInputModeManager } from './hooks/useInputModeManager';
import { FORM_FIELD_NAMES, getManualFieldName } from './formFieldNames';
import { CellType, TableSection } from './types';
import EditableTableCell from './EditableTableCell';
import ReadOnlyTableCell from './ReadOnlyTableCell';
import { useTableCellState } from './hooks/useTableCellState';
import { getCellType, getFieldName, getReadOnlyValue, calculateFieldDisabled } from './utils';
import { negativeOrNullValidator } from '@app/core/validations/hook-forms/validators';

interface CapitalStructureValuesTableCellProps {
  equityItem: EquityItem;
  renderingSplitType: OwnershipSplitType;
  isProRata: boolean;
  tableSection: TableSection;
  ownershipIndex: number;
  section: keyof typeof OwnerType | OwnershipSplitType.ProRata;
}

const CapitalStructureValuesTableCell: FC<CapitalStructureValuesTableCellProps> = ({
  equityItem,
  renderingSplitType,
  isProRata,
  tableSection,
  ownershipIndex,
  section,
}) => {
  const watchFieldNames = useMemo(() => {
    if (isProRata) {
      return [
        FORM_FIELD_NAMES.PRO_RATA_AMOUNT_INPUT_MODE,
        FORM_FIELD_NAMES.PRO_RATA_SHARES_INPUT_MODE,
      ];
    }
    return [
      getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_AMOUNT_INPUT_MODE),
      getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_SHARES_INPUT_MODE),
    ];
  }, [isProRata, ownershipIndex]);

  const watchedValues = useWatch({ name: watchFieldNames });
  const [amountMode, sharesMode] = watchedValues;

  const { isAmountField, inputModeState, shouldBeItalic } = useTableCellState(
    tableSection,
    renderingSplitType,
    isProRata,
    isProRata ? amountMode : null,
    isProRata ? sharesMode : null,
    !isProRata ? amountMode : null,
    !isProRata ? sharesMode : null
  );

  const fieldNames = useMemo(
    () => ({
      adjustmentAmountField: isProRata
        ? FORM_FIELD_NAMES.PRO_RATA_AMOUNT
        : getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_AMOUNT),
      adjustmentSharesField: isProRata
        ? FORM_FIELD_NAMES.PRO_RATA_SHARES
        : getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_SHARES),
      closingAmountField: isProRata
        ? FORM_FIELD_NAMES.PRO_RATA_CLOSING_AMOUNT
        : getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_CLOSING_AMOUNT),
      closingSharesField: isProRata
        ? FORM_FIELD_NAMES.PRO_RATA_CLOSING_SHARES
        : getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_CLOSING_SHARES),
    }),
    [isProRata, ownershipIndex]
  );

  useInputModeManager(isProRata, ownershipIndex, inputModeState, fieldNames);

  const cellType = getCellType(renderingSplitType, isProRata);
  const fieldName = getFieldName(isProRata, renderingSplitType, tableSection, ownershipIndex);
  const fieldDisabled = calculateFieldDisabled(inputModeState, tableSection);
  const readOnlyValue = getReadOnlyValue(equityItem, tableSection, section, isAmountField);
  const isRealBuybackValue = tableSection === TableSection.RealBuybackValue;

  const owner = equityItem.ownership[ownershipIndex];
  const hasOwnership = owner && owner.owner === section;
  const ownerExists = isProRata || hasOwnership;

  switch (cellType) {
    case CellType.EDITABLE:
      if (!ownerExists) {
        return <ReadOnlyTableCell value={readOnlyValue} isAmountField={isAmountField} />;
      }
      return (
        <EditableTableCell
          fieldName={fieldName}
          isAmountField={isAmountField}
          shouldBeItalic={shouldBeItalic}
          shouldBeDisabled={fieldDisabled}
          placeholder="0"
          rules={
            isRealBuybackValue
              ? { validate: { isNegative: negativeOrNullValidator(false).validate } }
              : undefined
          }
        />
      );

    case CellType.READ_ONLY:
      return <ReadOnlyTableCell value={readOnlyValue} isAmountField={isAmountField} />;

    case CellType.EMPTY:
    default:
      return <td />;
  }
};

export default CapitalStructureValuesTableCell;
